import React, { ReactElement, useEffect } from 'react';
import { Redirect, useLocation, useSearchParams } from 'wouter';
import { useAuth0 } from '@auth0/auth0-react';
import Loading from 'components/Loading';
import { useAccountStore } from 'store/useAccountStore';
import { AxiosError } from 'axios';
import { useAlyviaUserStore } from 'store/useAlyviaUseStore';
import { getAccountById } from 'requests/account';
import { getUserByAuth0Id } from 'requests/users';
import useToggleState from 'utils/hooks/useToggleState';
import { getAllActiveDevices } from 'requests/devices';
import { useSelectedDeviceStore } from 'store/useSelectedDeviceStore';
import showSnackbar from 'utils/showSnackbar';
import { redirectToAlyviaSSO } from 'utils';
import { alyviaAuth0Audience } from 'utils/constants';
import useAuthStore from 'store/useAuthStore';

type Props = {
  children: () => ReactElement;
};

export default function AuthWrapper({ children }: Props) {
  const [fetchAccountOrUserWithError, setFetchAccountOrUserWithError] =
    useToggleState(false);

  const [location] = useLocation();

  const isAuthRoute = ['/callback'].some((prefix) =>
    location.startsWith(prefix),
  );

  const isAuthenticatedRoute = ['/main'].some((prefix) =>
    location.startsWith(prefix),
  );

  const {
    isAuthenticated,
    isLoading,
    user: auth0User,
    getAccessTokenSilently,
  } = useAuth0();
  const { setAccount } = useAccountStore();
  const { alyviaUser, setUser } = useAlyviaUserStore();
  const { setSelectedDevice } = useSelectedDeviceStore();
  const [searchParams] = useSearchParams();
  const { setJwt, resetJwt } = useAuthStore();

  const defineUser = async (auth0Id: string) => {
    getUserByAuth0Id(auth0Id)
      .then((data) => {
        setUser(data);
      })
      .catch((error: AxiosError) => {
        console.error(error);
        setFetchAccountOrUserWithError();
      })
      .finally();
  };

  const defineSelectedDevice = async (accountId: string) => {
    getAllActiveDevices(accountId)
      .then((data) => {
        if (data?.length) {
          if (searchParams.get('deviceId')) {
            const device = data.filter(
              (device) => device.id === searchParams.get('deviceId'),
            )[0]?.id;
            if (device) {
              return setSelectedDevice(device);
            }
          }

          return setSelectedDevice(data[0].id);
        }
        showSnackbar(
          'Você não tem um dispositivo habilitado para o Alyvia Chat!',
          'warning',
        );
        redirectToAlyviaSSO();
      })
      .catch((error) => {
        console.error(error);
        showSnackbar('Falha ao buscar dispositivos conectados!', 'error');
      });
  };

  // Define the user's account when authenticated
  useEffect(() => {
    if (isAuthenticated && !isAuthRoute) {
      if (!alyviaUser) {
        defineUser(auth0User?.sub || '');
      } else {
        if (alyviaUser?.accountId) {
          getAccountById(alyviaUser.accountId)
            .then((data) => {
              setAccount(data);
              defineSelectedDevice(alyviaUser.accountId);
            })
            .catch((error: AxiosError) => {
              console.error(error);
              setFetchAccountOrUserWithError();
            })
            .finally();
        }
      }
    }
  }, [isAuthenticated, isLoading, alyviaUser, auth0User?.sub, setUser]);

  // checks every 10 seconds if the user session is valid
  useEffect(() => {
    if (isAuthenticatedRoute) {
      const intervalId = setInterval(async () => {
        try {
          const token = await getAccessTokenSilently({
            authorizationParams: {
              audience: alyviaAuth0Audience,
            },
            cacheMode: 'off',
          });
          setJwt(token);
        } catch (error) {
          showSnackbar('Sua sessão expirou!', 'info', 10000);
          clearInterval(intervalId);
          resetJwt();
          redirectToAlyviaSSO();
        }
      }, 30000);

      return () => {
        clearInterval(intervalId);
      };
    }
  }, [isAuthenticated]);

  if (
    !isAuthenticated &&
    !isLoading &&
    (!isAuthRoute || fetchAccountOrUserWithError)
  ) {
    return <Redirect to="/callback" />;
  }

  //Handle loading of auth0User
  if (!auth0User && !isAuthRoute) {
    return <Loading />;
  }

  return children();
}
